/**
 * LA COURBE DU MODULE ANALYSE — lot 99, 18/09/2026.
 *
 * Décisions du cadrage (doc 1) : des COURBES seulement, jamais de bâtons ;
 * grandes et lisibles (400 px, textes de 13 px) ; l'étiquette de valeur sur
 * la semaine en cours et au bout de chaque courbe ; un repère « aujourd'hui »,
 * la zone de prévision grisée et ses points creux ; une bulle au survol ; un
 * clic ouvre le détail.
 *
 * Générique : le BFR, la trésorerie et le résultat s'en servent.
 */

import { useEffect, useRef, useState } from 'react';

export interface Serie {
  readonly nom: string;
  /** Une des classes `serie-1` … `serie-4`. */
  readonly teinte: 1 | 2 | 3 | 4;
  readonly valeurs: readonly (number | null)[];
  readonly pointillee?: boolean;
}

export interface ProprietesCourbe {
  readonly titre: string;
  readonly sousTitre?: string;
  readonly etiquettes: readonly string[];
  readonly sousEtiquettes?: readonly string[];
  readonly series: readonly Serie[];
  /** Rang de la semaine en cours (repère « aujourd'hui »), ou -1. */
  readonly courant: number;
  /** Premier rang de prévision (zone grisée), ou `etiquettes.length`. */
  readonly futurDepuis: number;
  readonly format: (v: number) => string;
  readonly quandClic?: (rang: number) => void;
  /** Ligne horizontale de référence (seuil), facultative. */
  readonly seuils?: readonly { readonly valeur: number; readonly nom: string }[];
  readonly hauteur?: number;
}

function graduer(min: number, max: number): number[] {
  const etendue = max - min || Math.abs(max) || 1;
  const brut = etendue / 5;
  const base = Math.pow(10, Math.floor(Math.log10(brut)));
  const pas = [1, 2, 2.5, 5, 10].map((k) => k * base).find((k) => k >= brut) ?? base;
  const bas = Math.floor(min / pas) * pas;
  const haut = Math.ceil(max / pas) * pas;
  const res: number[] = [];
  for (let v = bas; v <= haut + pas / 2; v += pas) res.push(Math.round(v * 1e6) / 1e6);
  return res;
}

export function CourbeAnalyse({
  titre,
  sousTitre,
  etiquettes,
  sousEtiquettes,
  series,
  courant,
  futurDepuis,
  format,
  quandClic,
  seuils = [],
  hauteur = 400,
}: ProprietesCourbe) {
  const cadre = useRef<HTMLDivElement>(null);
  const [largeur, setLargeur] = useState(700);
  const [survol, setSurvol] = useState<number | null>(null);

  useEffect(() => {
    const zone = cadre.current;
    if (zone === null || typeof ResizeObserver === 'undefined') return;
    const o = new ResizeObserver((e) => {
      const w = e[0]?.contentRect.width;
      if (w !== undefined && w > 0) setLargeur(Math.round(w));
    });
    o.observe(zone);
    return () => o.disconnect();
  }, []);

  const L = Math.max(280, largeur);
  const H = hauteur;
  const etroit = L < 520;
  const g = etroit ? 58 : 76;
  const d = etroit ? 64 : 96;
  const h = 18;
  const b = 46;
  const n = etiquettes.length;
  const toutes = [
    ...series.flatMap((s) => s.valeurs.filter((v): v is number => v !== null)),
    ...seuils.map((s) => s.valeur),
    0,
  ];
  const ticks = graduer(Math.min(...toutes), Math.max(...toutes));
  const bas = ticks[0] ?? 0;
  const haut = ticks[ticks.length - 1] ?? 1;
  const col = (L - g - d) / Math.max(1, n);
  const x = (i: number) => g + col * i + col / 2;
  const y = (v: number) => h + (1 - (v - bas) / (haut - bas || 1)) * (H - h - b);
  const pasEtiquette = etroit ? Math.ceil(n / 5) : n > 14 ? 2 : 1;

  // LES ÉTIQUETTES DE VALEUR ne se chevauchent pas : à un même rang, deux
  // étiquettes à moins de 15 px l'une de l'autre sont écartées (lot 100 — la
  // trésorerie probable et prudente finissent souvent au même endroit).
  const hauteurs = new Map<string, number>();
  const rangs = [
    courant,
    ...series.map((s) =>
      s.valeurs.reduce<number>((r, v, i) => (v === null ? r : i), -1),
    ),
  ];
  for (const i of new Set(rangs.filter((r) => r >= 0))) {
    const ici = series
      .map((s, k) => ({ k, v: s.valeurs[i] }))
      .filter((e): e is { k: number; v: number } => e.v !== null && e.v !== undefined)
      .map((e) => ({ k: e.k, y: y(e.v) - 9 }))
      .sort((a, b) => a.y - b.y);
    let prec = -Infinity;
    for (const e of ici) {
      const yy = Math.max(e.y, prec + 15);
      hauteurs.set(`${String(e.k)}:${String(i)}`, yy);
      prec = yy;
    }
  }

  return (
    <figure className="courbe-analyse">
      <figcaption>
        <strong>{titre}</strong>
        {sousTitre !== undefined && <span>{sousTitre}</span>}
      </figcaption>
      <ul className="courbe-legende">
        {series.map((s) => (
          <li key={s.nom}>
            <span
              className={`courbe-cle serie-${String(s.teinte)}${s.pointillee === true ? ' pointillee' : ''}`}
            />
            {s.nom}
          </li>
        ))}
        {futurDepuis < n && (
          <li>
            <span className="courbe-cle courbe-cle-futur" />
            prévision
          </li>
        )}
      </ul>
      <div ref={cadre} className="courbe-dessin" onMouseLeave={() => setSurvol(null)}>
        <svg
          width={L}
          height={H}
          viewBox={`0 0 ${String(L)} ${String(H)}`}
          role="img"
          aria-label={titre}
        >
          {futurDepuis < n && (
            <rect
              x={g + col * futurDepuis}
              y={h}
              width={col * (n - futurDepuis)}
              height={H - h - b}
              className="courbe-futur"
            />
          )}
          {ticks.map((v) => (
            <g key={v}>
              <line
                x1={g}
                x2={L - d}
                y1={y(v)}
                y2={y(v)}
                className={v === 0 ? 'courbe-zero' : 'courbe-grille'}
              />
              <text x={g - 8} y={y(v) + 4} textAnchor="end" className="courbe-axe">
                {format(v)}
              </text>
            </g>
          ))}
          {seuils.map((s) => (
            <g key={s.nom}>
              <line
                x1={g}
                x2={L - d}
                y1={y(s.valeur)}
                y2={y(s.valeur)}
                className="courbe-seuil"
              />
              <text
                x={L - d + 4}
                y={y(s.valeur) + 4}
                className="courbe-axe courbe-seuil-nom"
              >
                {s.nom}
              </text>
            </g>
          ))}
          {courant >= 0 && (
            <g>
              <line
                x1={x(courant)}
                x2={x(courant)}
                y1={h}
                y2={H - b}
                className="courbe-aujourdhui"
              />
              <text
                x={x(courant)}
                y={h - 4}
                textAnchor="middle"
                className="courbe-axe courbe-aujourdhui-nom"
              >
                aujourd’hui
              </text>
            </g>
          )}
          {etiquettes.map((e, i) =>
            i % pasEtiquette === 0 || i === courant ? (
              <g key={e}>
                <text
                  x={x(i)}
                  y={H - b + 18}
                  textAnchor="middle"
                  className={`courbe-axe${i === courant ? ' fort' : ''}`}
                >
                  {e}
                </text>
                {sousEtiquettes?.[i] !== undefined && !etroit && (
                  <text
                    x={x(i)}
                    y={H - b + 34}
                    textAnchor="middle"
                    className="courbe-axe courbe-doux"
                  >
                    {sousEtiquettes[i]}
                  </text>
                )}
              </g>
            ) : null,
          )}
          {series.map((s, k) => {
            const pts = s.valeurs
              .map((v, i) =>
                v === null ? null : `${x(i).toFixed(1)},${y(v).toFixed(1)}`,
              )
              .filter((p): p is string => p !== null);
            const dernier = s.valeurs.reduce<number>(
              (r, v, i) => (v === null ? r : i),
              -1,
            );
            return (
              <g key={s.nom} className={`serie-${String(s.teinte)}`}>
                <polyline
                  points={pts.join(' ')}
                  className={`courbe-trait${s.pointillee === true ? ' pointillee' : ''}`}
                />
                {s.valeurs.map((v, i) =>
                  v === null ? null : (
                    <circle
                      key={i}
                      cx={x(i)}
                      cy={y(v)}
                      r={i === survol ? 5.5 : 4}
                      className={
                        i >= futurDepuis ? 'courbe-point creux' : 'courbe-point'
                      }
                    />
                  ),
                )}
                {[courant, dernier]
                  .filter((i, k, t) => i >= 0 && t.indexOf(i) === k)
                  .map((i) => {
                    const v = s.valeurs[i];
                    if (v === null || v === undefined) return null;
                    return (
                      <text
                        key={`e${String(i)}`}
                        x={i === dernier && i !== courant ? x(i) + 8 : x(i)}
                        y={hauteurs.get(`${String(k)}:${String(i)}`) ?? y(v) - 9}
                        textAnchor={i === dernier && i !== courant ? 'start' : 'middle'}
                        className="courbe-valeur"
                      >
                        {format(v)}
                      </text>
                    );
                  })}
              </g>
            );
          })}
          {etiquettes.map((e, i) => (
            <rect
              key={`z${e}`}
              x={g + col * i}
              y={h}
              width={col}
              height={H - h - b}
              className={`courbe-zone${quandClic === undefined ? '' : ' cliquable'}`}
              onMouseEnter={() => setSurvol(i)}
              onFocus={() => setSurvol(i)}
              onClick={() => quandClic?.(i)}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') quandClic?.(i);
              }}
              tabIndex={quandClic === undefined ? -1 : 0}
              role={quandClic === undefined ? undefined : 'button'}
              aria-label={`${e} : ${series.map((s) => `${s.nom} ${s.valeurs[i] === null || s.valeurs[i] === undefined ? '—' : format(s.valeurs[i] as number)}`).join(', ')}`}
            />
          ))}
        </svg>
        {survol !== null && (
          <div
            className="courbe-bulle"
            style={{ left: `${String(Math.min(Math.max(x(survol), 90), L - 90))}px` }}
            role="status"
          >
            <strong>
              {etiquettes[survol]}
              {sousEtiquettes?.[survol] !== undefined
                ? ` · ${sousEtiquettes[survol]}`
                : ''}
              {survol >= futurDepuis ? ' · prévision' : ''}
            </strong>
            {series.map((s) => (
              <span key={s.nom}>
                <span className={`courbe-cle serie-${String(s.teinte)}`} />
                {s.nom} :{' '}
                {s.valeurs[survol] === null || s.valeurs[survol] === undefined
                  ? '—'
                  : format(s.valeurs[survol] as number)}
              </span>
            ))}
            {quandClic !== undefined && <em>Cliquer pour le détail</em>}
          </div>
        )}
      </div>
    </figure>
  );
}
